import { useCommandPaletteStore } from '@/components/command-palette/commandPaletteStore'
import { useClearFilters, usePageContext } from '@/components/page-context/context'
import { announce } from '@/lib/announcer'
import { useShortcut } from '@/lib/useShortcut'
import { useAuthStore } from '@/stores/authStore'
import { useCallback } from 'react'

/**
 * `C`, `/` and Alt+Shift+Q: none of them own a visible control the way the palette or sidebar toggle
 * do, so they live here instead of inside the component they affect. All three are scoped to a
 * focused control the same way: a dialog, menu or popover keeps them for itself.
 */
export function GeneralShortcutsHost() {
	const { hasMainSearch, focusMainSearch } = useClearFilters()
	const { openToolbar } = usePageContext()

	const onCreate = useCallback((event: KeyboardEvent) => {
		event.preventDefault()
		useCommandPaletteStore.getState().openWithView('create')
		announce('Create')
	}, [])
	useShortcut('create.open', onCreate, { skipInOverlay: true })

	// Falls back to the palette on a page with no main search, per the RFC. Otherwise opens a collapsed
	// header first, so the search it reveals is not focused while invisible. The focus waits a frame for
	// that open to actually land in the DOM — focusing synchronously reads the search's pre-open position,
	// which is what a filter popover it opens would otherwise anchor itself to.
	const onFocusSearch = useCallback(
		(event: KeyboardEvent) => {
			event.preventDefault()
			if (!hasMainSearch()) {
				useCommandPaletteStore.getState().setOpen(true)
				return
			}
			openToolbar?.()
			requestAnimationFrame(() => focusMainSearch())
		},
		[hasMainSearch, focusMainSearch, openToolbar],
	)
	useShortcut('search.focus', onFocusSearch, { skipInOverlay: true })

	const onLogout = useCallback((event: KeyboardEvent) => {
		event.preventDefault()
		void useAuthStore.getState().logout()
	}, [])
	useShortcut('auth.logout', onLogout, { skipInOverlay: true })

	return null
}
